/**
 * Utilitário para gerenciar URLs de checkout com validação de expiração
 * 
 * As URLs de checkout são válidas por 1 hora (3600000 ms) após serem salvas.
 * Após esse período, elas são consideradas expiradas e removidas automaticamente.
 */

const CHECKOUT_URL_KEY = 'checkoutUrl';
const CHECKOUT_TIMESTAMP_KEY = 'checkoutUrlTimestamp';
const PREVIOUS_URL_KEY = 'previousUrl';
const EXPIRATION_TIME_MS = 60 * 60 * 1000; // 1 hora em milissegundos

/**
 * Salva a URL de checkout no localStorage com timestamp de criação
 * @param {string} url - URL de checkout a ser salva
 */
export const saveCheckoutUrl = (url) => {
  if (!url) {
    console.warn('Tentativa de salvar URL de checkout vazia');
    return;
  }

  const timestamp = Date.now();
  localStorage.setItem(CHECKOUT_URL_KEY, url);
  localStorage.setItem(CHECKOUT_TIMESTAMP_KEY, timestamp.toString());
  
  console.log('✅ URL de checkout salva:', url, 'Timestamp:', new Date(timestamp).toLocaleString('pt-BR'));
};

/**
 * Verifica se a URL de checkout ainda é válida (não expirou)
 * @returns {boolean} true se a URL existe e não expirou, false caso contrário
 */
export const isCheckoutUrlValid = () => {
  const url = localStorage.getItem(CHECKOUT_URL_KEY);
  const timestampStr = localStorage.getItem(CHECKOUT_TIMESTAMP_KEY);

  if (!url || !timestampStr) {
    return false;
  }

  const timestamp = parseInt(timestampStr, 10);
  const now = Date.now();
  const elapsed = now - timestamp;

  if (elapsed > EXPIRATION_TIME_MS) {
    console.log('⏰ URL de checkout expirada. Tempo decorrido:', Math.round(elapsed / 1000 / 60), 'minutos');
    // Limpar URLs expiradas
    clearCheckoutUrl();
    return false;
  }

  return true;
};

/**
 * Obtém a URL de checkout se ela ainda for válida
 * @returns {string|null} URL de checkout válida ou null se expirada/não existir
 */
export const getCheckoutUrl = () => {
  if (!isCheckoutUrlValid()) {
    return null;
  }

  return localStorage.getItem(CHECKOUT_URL_KEY);
};

/**
 * Remove a URL de checkout e seu timestamp do localStorage
 */
export const clearCheckoutUrl = () => {
  localStorage.removeItem(CHECKOUT_URL_KEY);
  localStorage.removeItem(CHECKOUT_TIMESTAMP_KEY);
  localStorage.removeItem(PREVIOUS_URL_KEY);
  console.log('🗑️ URL de checkout removida do localStorage');
};

/**
 * Salva a URL anterior (página de onde o usuário veio antes do checkout)
 * @param {string} url - URL anterior a ser salva
 */
export const savePreviousUrl = (url) => {
  if (!url) {
    console.warn('Tentativa de salvar URL anterior vazia');
    return;
  }
  localStorage.setItem(PREVIOUS_URL_KEY, url);
  console.log('✅ URL anterior salva:', url);
};

/**
 * Obtém a URL anterior (página de onde o usuário veio antes do checkout)
 * @returns {string|null} URL anterior ou null se não existir
 */
export const getPreviousUrl = () => {
  return localStorage.getItem(PREVIOUS_URL_KEY);
};

/**
 * Obtém informações sobre a URL de checkout (para debug)
 * @returns {object|null} Objeto com url, timestamp e tempo restante, ou null se não existir
 */
export const getCheckoutUrlInfo = () => {
  const url = localStorage.getItem(CHECKOUT_URL_KEY);
  const timestampStr = localStorage.getItem(CHECKOUT_TIMESTAMP_KEY);

  if (!url || !timestampStr) {
    return null;
  }

  const timestamp = parseInt(timestampStr, 10);
  const now = Date.now();
  const elapsed = now - timestamp;
  const remaining = EXPIRATION_TIME_MS - elapsed;
  const remainingMinutes = Math.max(0, Math.round(remaining / 1000 / 60));

  return {
    url,
    timestamp,
    createdAt: new Date(timestamp).toLocaleString('pt-BR'),
    elapsedMs: elapsed,
    remainingMs: remaining,
    remainingMinutes,
    isValid: remaining > 0,
  };
};

